import { shopifyQueryKey, getVerifiedTestFeedConnectionScope } from '../ShopifyAppContext'
/**
 * What: Loads the TestFeed-owned feature entitlement snapshot for Shopify UI gating.
 * Transport / data-flow: Browser hook over `/api/testfeed-entitlements`; falls back to fail-closed local defaults while loading.
 *
 */
import {
  buildDefaultShopifyEntitlementSnapshot,
  type FeatureEntitlementSnapshot,
} from '@testfeed/entitlement-contracts'
import { useQuery } from '@tanstack/react-query'
import { shopifyApiFetch } from '../shopifyApiFetch'
import { shopifyQueryPolicies } from '../queries/shopifyQueryPolicies'

export function shopifyEntitlementsQueryKey(
  shopDomain: string,
  orgId: string | null,
  connectionId: string | null,
  verified: boolean,
) {
  return shopifyQueryKey(shopDomain || 'no-shop', orgId || 'no-org', connectionId || 'no-connection', [
    'testfeed-entitlements',
    verified,
  ] as const)
}

type ShopifyTokenSource = Parameters<typeof shopifyApiFetch>[0]

type TestFeedEntitlementsPayload = {
  entitlements: FeatureEntitlementSnapshot
}

type UseShopifyEntitlementsInput = {
  isTestFeedConnected: boolean
  orgId?: string | null
  shopDomain?: string | null
  connectionId?: string | null
}

async function loadEntitlements(shopify: ShopifyTokenSource): Promise<FeatureEntitlementSnapshot> {
  const response = await shopifyApiFetch(shopify, '/api/testfeed-entitlements')
  if (response.ok) {
    const payload = (await response.json()) as TestFeedEntitlementsPayload
    return payload.entitlements
  }

  const payload = (await response.json().catch((error) => {
    console.error(error)
    return {}
  })) as { error?: unknown }
  const message = typeof payload.error === 'string' ? payload.error : `Entitlements request failed (${response.status})`
  throw new Error(message)
}

/** Provides one capability snapshot for Settings, nav, direct routes, and action guards. */
export function useShopifyEntitlements(shopify: ShopifyTokenSource, input: UseShopifyEntitlementsInput) {
  const verifiedScope = getVerifiedTestFeedConnectionScope(
    {
      ready: input.isTestFeedConnected,
      code: input.isTestFeedConnected ? 'READY' : 'NOT_READY',
      externalAccountId: input.shopDomain,
      orgId: input.orgId,
      connectionId: input.connectionId,
    },
    input.shopDomain,
  )

  const fallback = buildDefaultShopifyEntitlementSnapshot({
    connectionStatus: verifiedScope ? 'active' : 'missing',
    orgId: verifiedScope?.orgId ?? null,
    shopDomain: verifiedScope?.shopDomain ?? null,
    connectionId: verifiedScope?.connectionId ?? null,
  })

  const query = useQuery({
    queryKey: shopifyEntitlementsQueryKey(
      verifiedScope?.shopDomain || 'no-shop',
      verifiedScope?.orgId || null,
      verifiedScope?.connectionId || null,
      !!verifiedScope,
    ),
    queryFn: () => loadEntitlements(shopify),
    placeholderData: fallback,
    enabled: !!verifiedScope,
    ...shopifyQueryPolicies.entitlements,
  })

  return {
    ...query,
    data: query.data ?? fallback,
  }
}
